import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchScanIfNeeded,
  requestScanSummary,
  resetAiScannerAnswers,
  startAiScannerSession
} from '../../actions/scan';
import { fetchSymbolIfNeeded } from '../../actions/symbol';
import HASHTAG_FILTER_CONFIG from '../../constants/hashtagFilterConfig';
import { getScansForSelection } from '../../constants/hashtagScanMap';
import aiAssistantFabIcon from '../../icons/Icon-AI-Assistant-FAB.svg';
import closeIcon from '../../icons/Icon-AI-Scanner-Close.svg';
import {
  filterSelectedHashtags,
  getRequiredScans
} from '../../utils/hashtagFilterEngine';
import { useFeatureToggle } from '../../utils/hooks/useFeatureToggle';
import {
  buildSummarizeRequest,
  MAX_SUMMARY_SYMBOLS
} from '../../utils/summarizePayload';
import useLocalize from '../../utils/hooks/useLocalize';
import useOnboardingSeen from '../../utils/hooks/useOnboardingSeen';
import AiScannerCoachMark from './AiScannerCoachMark';
import HashtagChips, { HASHTAG_DEFINITIONS } from './HashtagChips';
import InvestmentQuestions from './InvestmentQuestions';

const FAB_BOTTOM_OFFSET = 34;
const MOBILE_FOOTER_ID = 'gnavi-mobile-footer';
const SHEET_PEEK = 44; // visible height when collapsed (px)
const SHEET_TOP_MARGIN = 40; // gap from overlay top when expanded (px)

/**
 * AiScannerContainer Component
 *
 * Renders a fixed floating "AI アシスタント" button at the bottom-right of the
 * Scanner page at the smartphone breakpoint. Tapping the button opens the AI
 * Scanner chat overlay. The overlay can be dismissed via the ✕ icon at the
 * top-right.
 *
 * While the footer is not in view the button stays fixed at the bottom of the
 * viewport (bottom: 34px via CSS). Once the footer scrolls into view the
 * `bottom` value is increased so the button maintains the same 34px gap from
 * the top of the footer instead of from the viewport edge.
 *
 * On the user's first open a two-step onboarding coach mark (see
 * {@link AiScannerCoachMark}) is layered on top of the main blank overlay.
 * The "seen" flag is persisted per user via {@link useOnboardingSeen} and is
 * only recorded when the user completes the onboarding (taps "AI Chatbot を
 * はじめる") or closes the overlay via ✕. Leaving mid-flow does not record
 * the flag, so the onboarding resumes from step 1 on the next visit.
 *
 * @returns {JSX.Element} The AI Scanner FAB and, when open, the overlay.
 */
export const AiScannerContainer = () => {
  const { isFeatureEnabled } = useFeatureToggle('aiScannerPopsicleEnabled');
  const { hasSeen, markSeen, userId } = useOnboardingSeen();
  const localize = useLocalize();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [fabBottomOverride, setFabBottomOverride] = useState(null);
  // Locally track whether the coach mark is showing for the CURRENT open
  // session. This lets the "seen" flag be flipped in localStorage before we
  // unmount without immediately re-rendering the main chat mid-transition.
  const [showCoachMark, setShowCoachMark] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(null); // px from top while dragging; null = snapped
  const [sheetDragging, setSheetDragging] = useState(false);
  // STORY-009: true once the member taps 「NNN 件を表示」 in the hashtag sheet.
  const [showQuestions, setShowQuestions] = useState(false);
  const sheetRef = useRef(null);
  const questionsRef = useRef(null);
  const sheetExpandedRef = useRef(false);
  const dragRef = useRef({
    active: false, startY: 0, startOffset: 0, lastY: 0, lastT: 0, velocity: 0
  });

  // Helper to keep the expanded ref in sync for pointer event handlers
  const updateSheetExpanded = useCallback((value) => {
    sheetExpandedRef.current = value;
    setSheetExpanded(value);
  }, []);

  // STORY-009: tearing down the questions step is two steps, not one — the
  // answers live in the store rather than in this component, so clearing
  // `showQuestions` on its own would leave the pair behind. Kept together so
  // the three callers (user switch, open, close) cannot drift apart.
  const resetQuestionsFlow = useCallback(() => {
    setShowQuestions(false);
    dispatch(resetAiScannerAnswers());
  }, [dispatch]);

  // Reset the overlay when the active user changes (e.g., switching between
  // user sessions on the same device without a full page reload) so state
  // from the previous user's session does not carry over.
  useEffect(() => {
    setIsOpen(false);
    setShowCoachMark(false);
    updateSheetExpanded(false);
    setSheetOffset(null);
    setSheetDragging(false);
    // Unobservable today — `handleOpen` resets too, and the questions only
    // render after it — but the answers are session state like everything
    // else reset here.
    resetQuestionsFlow();
  }, [resetQuestionsFlow, userId, updateSheetExpanded]);

  useEffect(() => {
    if (!isFeatureEnabled) {
      return;
    }

    let rafId = null;

    const updateFabPosition = () => {
      const footer = document.getElementById(MOBILE_FOOTER_ID);
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      if (footerRect.top < window.innerHeight) {
        setFabBottomOverride(window.innerHeight - footerRect.top + FAB_BOTTOM_OFFSET);
      } else {
        setFabBottomOverride(null);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        updateFabPosition();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateFabPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isFeatureEnabled]);

  // STORY-008: Selection state feeds the live-count derivation further down.
  const [selectedHashtagIds, setSelectedHashtagIds] = useState(() => new Set());

  // STORY-013 (#713): the previous selection, so the change handler can tell an
  // ADD from a REMOVE. Held in a ref rather than read from `selectedHashtagIds`
  // so the callback keeps a stable identity (a new `onSelectionChange` would
  // re-render HashtagChips for nothing).
  const previousSelection = useRef(new Set());

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // STORY-013 (#713): HashtagChips unmounts with the overlay and remounts
    // with an empty selection, so the container's view of the selection has to
    // be reset too. Without this, re-tapping a chip that was selected before
    // the overlay closed would read as "not grown" and skip the fetch pass —
    // which also skips the retry for a scan that previously errored.
    previousSelection.current = new Set();
    setSelectedHashtagIds(new Set());
    // STORY-009: a reopened overlay starts a fresh conversation, so neither the
    // questions step nor the previous answers carry over.
    resetQuestionsFlow();
    // Show the coach mark only for users who have not yet seen it on this
    // device. Returning users skip straight to the chat.
    setShowCoachMark(!hasSeen);
  }, [hasSeen, resetQuestionsFlow]);

  const handleClose = useCallback(() => {
    // ✕ during onboarding counts as a completion for "seen" tracking, per
    // FR-007 / Rule 4. If the coach mark was already dismissed this session,
    // don't call markSeen again — the hook itself is idempotent, but skipping
    // the call keeps container behavior explicit and testable.
    if (showCoachMark) {
      markSeen();
    }
    setShowCoachMark(false);
    resetQuestionsFlow();
    setIsOpen(false);
  }, [markSeen, resetQuestionsFlow, showCoachMark]);

  const handleCoachMarkComplete = useCallback(() => {
    markSeen();
    setShowCoachMark(false);
  }, [markSeen]);

  // --- Bottom sheet snap positions (relative to overlay top) ---
  const getSheetSnaps = useCallback(() => {
    const h = sheetRef.current?.parentElement?.clientHeight || window.innerHeight;
    return { open: SHEET_TOP_MARGIN, closed: h - SHEET_PEEK };
  }, []);

  // Tapping the hashtag link programmatically expands the sheet.
  const handleExpandSheet = useCallback(() => {
    updateSheetExpanded(true);
    setSheetOffset(null);
  }, [updateSheetExpanded]);

  // STORY-009: 「NNN 件を表示」 collapses the sheet and starts the question flow.
  const handleShowResults = useCallback(() => {
    updateSheetExpanded(false);
    setSheetOffset(null);
    setShowQuestions(true);
  }, [updateSheetExpanded]);

  // STORY-008: memoized derivation of live count / loading / error.
  //
  // We deliberately do NOT wire this through a reselect selector — reading the
  // store slices with plain `useSelector` and deriving with `useMemo` below
  // keeps the data flow explicit and re-evaluates on every dependency change.
  const scanData = useSelector(
    (state) => (state && state.scans && state.scans.scanData) || null
  );
  const scanErrors = useSelector(
    (state) => (state && state.scans && state.scans.scanErrors) || null
  );

  // STORY-009: watched so the newest message can be scrolled into view when Q2
  // appears. The answers themselves belong to InvestmentQuestions.
  const answeredQ1 = useSelector(
    (state) =>
      (state && state.scans && state.scans.aiScannerAnswers &&
        state.scans.aiScannerAnswers.q1) || null
  );

  // STORY-011 (#719): inputs for the summarize request. The session id is
  // minted when the questions step opens; the symbol metadata supplies the
  // `name` Syphon requires on every entry.
  const aiScannerSessionId = useSelector(
    (state) => (state && state.scans && state.scans.aiScannerSessionId) || null
  );
  const symbolMetadata = useSelector(
    (state) => (state && state.symbols && state.symbols.data) || null
  );

  // STORY-009: hashtag labels for the header message, in selection order.
  const selectedTagLabels = useMemo(
    () =>
      Array.from(selectedHashtagIds)
        .map((id) => HASHTAG_DEFINITIONS.find((tag) => tag.id === id))
        .filter(Boolean)
        .map((tag) => localize(tag.labelKey)),
    [localize, selectedHashtagIds]
  );

  // Rule 5: keep the latest message visible as the conversation grows.
  useEffect(() => {
    if (!showQuestions) return;
    questionsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [answeredQ1, showQuestions]);

  // STORY-007: Fire de-duplicated scan requests when hashtag selection changes.
  // STORY-013 (#713): scanName -> the in-flight request promise, so two rapid
  // selections needing the same scan share one Syphon call instead of the
  // second silently relying on the first to land. `shouldFetchScan`
  // deliberately does not consider in-flight state, so this is the only place
  // concurrent duplicates are collapsed.
  // User/token validation is handled inside fetchScan (see actions/scan.js).
  const inFlightScans = useRef(new Map());

  const handleHashtagSelectionChange = useCallback(
    (selectedIds) => {
      const nextSelection = new Set(selectedIds);
      const previous = previousSelection.current;
      const selectionGrew = Array.from(nextSelection).some(
        (id) => !previous.has(id)
      );

      previousSelection.current = nextSelection;
      setSelectedHashtagIds(nextSelection);

      // STORY-013 (#713) AC C19820799 / C19820801: deselecting issues no
      // requests at all. The scans required by a smaller selection are always
      // a SUBSET of those required by the larger one, so a removal can never
      // produce a cache miss — the only thing it could trigger is a staleness
      // refetch of data we are already displaying. Skipping the pass entirely
      // keeps those ACs true regardless of how long the user took.
      //
      // Opening the panel is not a separate trigger: HashtagChips unmounts
      // with the overlay and remounts with an empty selection, so the first
      // ADD is always the entry point.
      if (!selectionGrew) return;

      // Fire scan requests immediately (STORY-007 behavior preserved). The
      // whole selection is offered to `fetchScanIfNeeded`, not just the newly
      // required scans, so anything that has gone stale is refreshed too.
      const scanNames = getScansForSelection(nextSelection);
      if (scanNames.length === 0) return;

      const inFlight = inFlightScans.current;

      scanNames.forEach((scanName) => {
        if (inFlight.has(scanName)) return;

        // Freshness lives in the thunk (`shouldFetchScan`), so a scan that is
        // still fresh resolves without touching Syphon.
        const result = dispatch(fetchScanIfNeeded(scanName));
        const settled =
          result && typeof result.then === 'function'
            ? result
            : Promise.resolve(result);
        inFlight.set(scanName, settled);
        // No render trigger needed on settle: success updates `scanData` and
        // failure updates `scanErrors`, and both re-render via useSelector.
        settled
          .finally(() => {
            inFlight.delete(scanName);
          })
          // `fetchScan` catches its own failures, so this only fires if
          // something throws inside that catch (e.g.
          // redirectToLoginIfRequired). Swallow it rather than emitting an
          // unhandled rejection.
          .catch(() => {});
      });
    },
    [dispatch]
  );

  const filterResult = useMemo(() => {
    const empty = {
      count: 0,
      symbols: [],
      isLoading: false,
      hasError: false,
      missingScans: []
    };
    if (!selectedHashtagIds || selectedHashtagIds.size === 0) return empty;

    const data = scanData || {};
    const errors = scanErrors || {};
    const required = getRequiredScans(
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );

    // STORY-013 (#713): "no usable data yet" is exactly the loading condition,
    // so no separate in-flight check is needed and the count never regresses to
    // `---` for data we already hold — a stale scan keeps showing its previous
    // count while the refresh is in flight.
    //
    // Tests `.symbols`, not just presence, to stay in step with the other two
    // places that ask the same question: `shouldFetchScan` (actions/scan.js)
    // and `shouldFetchField` (desktopScans/ScanResultsSection.js). An entry
    // without symbols must read as "still loading", not as a zero-match result.
    const missing = required.filter((s) => !data[s.toLowerCase()]?.symbols);

    // Errors are only fatal for scans with nothing cached. `shouldFetchScan`
    // refetches a scan once it passes SCAN_STALE_SECONDS, so a scan whose
    // symbols are already on screen can pick up an error from a *refresh* that
    // failed. Checking `required` here instead of `missing` let that discard a
    // count the member was already looking at, and disable the submit button
    // with usable data still in the store — extending the promise in the
    // comment above from "refresh in flight" to "refresh failed".
    if (missing.some((s) => errors[s.toLowerCase()])) {
      return {
        count: null,
        symbols: [],
        isLoading: false,
        hasError: true,
        missingScans: []
      };
    }

    if (missing.length > 0) {
      return {
        count: null,
        symbols: [],
        isLoading: true,
        hasError: false,
        missingScans: missing
      };
    }

    const symbols = filterSelectedHashtags(
      data,
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );
    return {
      count: symbols.length,
      symbols,
      isLoading: false,
      hasError: false,
      missingScans: []
    };
  }, [scanData, scanErrors, selectedHashtagIds]);

  // STORY-011 (#719): entering the questions step opens a conversation.
  //
  // The symbol names are prefetched HERE rather than at Q2 because
  // `fetchSymbolIfNeeded` batches through a debounce timer and cannot be
  // awaited — firing them while the member reads Q1 leaves several seconds for
  // the batch to land, so `name` is in the store by the time the payload is
  // assembled. A symbol still missing one is dropped by the assembler rather
  // than sent with an empty name, which Syphon rejects.
  useEffect(() => {
    if (!showQuestions) return;
    dispatch(startAiScannerSession());
    // Keyed on `showQuestions` alone: the session belongs to the step, not to
    // the candidate list, which keeps re-deriving as scans land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showQuestions]);

  // The candidates actually sent. Shared with the prefetch effect below so the
  // two cannot disagree about which symbols need a name.
  const summarySymbols = useMemo(
    () => filterResult.symbols.slice(0, MAX_SUMMARY_SYMBOLS),
    [filterResult.symbols]
  );

  useEffect(() => {
    if (!showQuestions) return;
    // Re-runs when the candidate list changes: a snapshot taken once on entry
    // could name a different five than the payload ends up carrying, and a
    // candidate with no name is skipped outright rather than backfilled — so
    // the two lists have to stay in step or the request silently goes short.
    summarySymbols.forEach((symbolId) => {
      dispatch(fetchSymbolIfNeeded(symbolId));
    });
  }, [dispatch, showQuestions, summarySymbols]);

  // STORY-011 (#719): both questions answered — assemble and send.
  //
  // Also fires when Q1 CHANGES after Q2 was answered, so this can re-request
  // while an earlier call is still in flight. Ordering is handled in the
  // reducer, which drops any result superseded by a newer request.
  const handleAnswersComplete = useCallback(
    (answers) => {
      const body = buildSummarizeRequest({
        sessionId: aiScannerSessionId,
        answers: answers,
        selectedHashtagIds: selectedHashtagIds,
        symbols: summarySymbols,
        scanData: scanData,
        symbolMetadata: symbolMetadata,
        localize: localize
      });
      dispatch(requestScanSummary(body));
    },
    [
      aiScannerSessionId,
      dispatch,
      summarySymbols,
      localize,
      scanData,
      selectedHashtagIds,
      symbolMetadata
    ]
  );

  // --- Pointer-event drag handlers for the bottom sheet ---
  const handleSheetPointerDown = useCallback((e) => {
    const { open, closed } = getSheetSnaps();
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startOffset: sheetExpandedRef.current ? open : closed,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
    };
    setSheetDragging(true);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [getSheetSnaps]);

  const handleSheetPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const { open, closed } = getSheetSnaps();
    const now = performance.now();
    const dt = now - dragRef.current.lastT || 16;
    dragRef.current.velocity = (e.clientY - dragRef.current.lastY) / dt;
    dragRef.current.lastY = e.clientY;
    dragRef.current.lastT = now;

    let next = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    next = Math.max(open, Math.min(closed, next));
    setSheetOffset(next);
  }, [getSheetSnaps]);

  const handleSheetPointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setSheetDragging(false);

    const { open, closed } = getSheetSnaps();
    const v = dragRef.current.velocity;
    const traveled = Math.abs(e.clientY - dragRef.current.startY);
    // Compute current position from drag data to avoid stale closure on sheetOffset
    const current = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    const wasExpanded = sheetExpandedRef.current;

    let willExpand;
    if (traveled < 6) {
      // Barely moved — treat as a tap and toggle.
      willExpand = !wasExpanded;
    } else if (Math.abs(v) > 0.5) {
      // Fast flick wins regardless of position.
      willExpand = v < 0;
    } else {
      // Slow drag — snap to whichever half is closer.
      willExpand = current < (open + closed) / 2;
    }

    updateSheetExpanded(willExpand);
    setSheetOffset(null);
  }, [getSheetSnaps, updateSheetExpanded]);

  // Escape key collapses the sheet
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && sheetExpanded) {
        updateSheetExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, sheetExpanded, updateSheetExpanded]);

  // Move focus into the sheet when it expands so keyboard/screen-reader users
  // are aware the content is available. The sheet container itself receives
  // focus (tabIndex={-1} below).
  useEffect(() => {
    if (sheetExpanded) {
      sheetRef.current?.focus();
    }
  }, [sheetExpanded]);

  if (!isFeatureEnabled) {
    return null;
  }

  // Compute bottom sheet translateY
  const { open: snapOpen, closed: snapClosed } = getSheetSnaps();
  const sheetY = sheetOffset ?? (sheetExpanded ? snapOpen : snapClosed);
  const sheetStyle = {
    transform: `translateY(${sheetY}px)`,
    transition: sheetDragging ? 'none' : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
  };

  return (
    <div className="ai-scanner-container">
      <button
        className="ai-scanner-fab"
        style={fabBottomOverride !== null ? { bottom: `${fabBottomOverride}px` } : 
undefined}
        onClick={handleOpen}
        aria-label="AIアシスタントを開く"
        aria-expanded={isOpen}
      >
        <img
          src={aiAssistantFabIcon}
          alt=""
          aria-hidden="true"
          width="68"
          height="68"
        />
      </button>
      {isOpen && (
        <div
          className={`ai-scanner-overlay${
            showQuestions ? ' ai-scanner-overlay--questions' : ''
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="AIスキャナー"
        >
          <button
            className="ai-scanner-overlay-close"
            onClick={handleClose}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
          <div className="ai-scanner-overlay-content">
            {!showQuestions && (
              <>
                <p className="ai-scanner-overlay-greeting">
                  {localize('ai.scanner.greeting')}
                </p>
                <button
                  className="ai-scanner-overlay-hashtag-link"
                  onClick={handleExpandSheet}
                  type="button"
                >
                  {localize('ai.scanner.hashtag.link')}
                </button>
              </>
            )}
            {showQuestions && (
              <div ref={questionsRef} className="ai-scanner-questions-anchor">
                <InvestmentQuestions
                  selectedTags={selectedTagLabels}
                  matchCount={filterResult.count}
                  onAnswersComplete={handleAnswersComplete}
                />
              </div>
            )}
          </div>
          <div
            className={`ai-scanner-overlay-swipe-area${
              sheetExpanded ? ' ai-scanner-overlay-swipe-area--expanded' : ''
            }`}
            ref={sheetRef}
            style={{ '--sheet-top-margin': `${SHEET_TOP_MARGIN}px`, ...sheetStyle }}
            tabIndex={-1}
            role={sheetExpanded ? 'region' : undefined}
            aria-labelledby={sheetExpanded ? 'ai-scanner-sheet-title' : undefined}
          >
            <div
              className="ai-scanner-overlay-swipe-area-handle"
              onPointerDown={handleSheetPointerDown}
              onPointerMove={handleSheetPointerMove}
              onPointerUp={handleSheetPointerUp}
              onPointerCancel={handleSheetPointerUp}
              role="button"
              tabIndex={0}
              aria-expanded={sheetExpanded}
              aria-label={sheetExpanded ? localize('button.label.close') : localize('ai.scanner.hashtag.modal.open')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  updateSheetExpanded(!sheetExpanded);
                }
              }}
            >
              <div className="ai-scanner-overlay-swipe-area-grabber" />
            </div>
            <div
              className="ai-scanner-overlay-swipe-area-body"
              aria-hidden={!sheetExpanded}
              style={{
                opacity: sheetExpanded || sheetOffset !== null ? 1 : 0,
                overflowY: 'hidden',
              }}
            >
              <div className="ai-scanner-overlay-swipe-area-content">
                <p id="ai-scanner-sheet-title" className="ai-scanner-overlay-swipe-area-title">
                  {localize('ai.scanner.hashtag.modal.title')}
                </p>
                <HashtagChips
                  onSelectionChange={handleHashtagSelectionChange}
                  onShowResults={handleShowResults}
                  matchCount={filterResult.count}
                  isLoading={filterResult.isLoading}
                  hasError={filterResult.hasError}
                />
              </div>
            </div>
          </div>
          {showCoachMark && (
            <AiScannerCoachMark onComplete={handleCoachMarkComplete} />
          )}
        </div>
      )}
    </div>
  );
};

export default AiScannerContainer;
